/* ============================================================================
   HONOR ATHLETICS — Ambassador Page Scripts
   Version: v10 Profile Collage / Shopify Customizer Safe

   Handles:
   - Scroll reveal animation
   - Cursor-following Fluent hover highlight
   - Smooth anchor scroll
   - ?apply=1 auto-scroll to application form
   - Form success scroll
   - Empty top CTA fallback
   - Shopify Theme Editor section reload support
   ============================================================================ */

(function () {
  'use strict';

  function initAmbassadorPage(root) {
    var scopes = getScopes(root);

    if (!scopes.length) return;

    scopes.forEach(function (scope) {
      initTopCtaFallback(scope);
      initReveal(scope);
      initHoverReveal(scope);
      initSmoothAnchors(scope);
      initApplyScroll(scope);
      initSuccessScroll(scope);
    });
  }

  function getScopes(root) {
    var scopes = [];

    if (root && root.classList && root.classList.contains('ha-scope')) {
      scopes.push(root);
    }

    if (root && root.querySelectorAll) {
      scopes = scopes.concat(Array.prototype.slice.call(root.querySelectorAll('.ha-scope')));
    }

    if (!scopes.length) {
      scopes = Array.prototype.slice.call(document.querySelectorAll('.ha-scope'));
    }

    return uniqueElements(scopes);
  }

  function uniqueElements(items) {
    var unique = [];

    items.forEach(function (item) {
      if (item && unique.indexOf(item) === -1) {
        unique.push(item);
      }
    });

    return unique;
  }

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  /* --------------------------------------------------------------------------
     Empty CTA fallback
     Fixes empty black/yellow pill if Customizer text is accidentally blank.
     -------------------------------------------------------------------------- */
  function initTopCtaFallback(scope) {
    var ctas = Array.prototype.slice.call(scope.querySelectorAll('.ha-topbar-cta'));

    ctas.forEach(function (cta) {
      var text = normalizeText(cta.textContent);

      if (!text) {
        cta.textContent = 'Start Application';
      }

      if (!cta.getAttribute('href')) {
        cta.setAttribute('href', '#apply');
      }

      cta.setAttribute('aria-label', normalizeText(cta.textContent) || 'Start Application');
    });
  }

  /* --------------------------------------------------------------------------
     Scroll reveal
     Adds .in when .ha-rise elements enter viewport.
     -------------------------------------------------------------------------- */
  function initReveal(scope) {
    var items = Array.prototype.slice.call(scope.querySelectorAll('.ha-rise'));

    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('in');
      });
      return;
    }

    if (scope.__haRevealObserver) {
      scope.__haRevealObserver.disconnect();
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var parent = el.parentNode;
        var siblings = parent
          ? Array.prototype.slice.call(parent.querySelectorAll('.ha-rise'))
          : items;

        var idx = siblings.indexOf(el);

        el.style.transitionDelay = Math.min(Math.max(idx, 0), 6) * 80 + 'ms';
        el.classList.add('in');

        io.unobserve(el);
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -60px 0px'
    });

    items.forEach(function (el) {
      if (!el.classList.contains('in')) {
        io.observe(el);
      }
    });

    scope.__haRevealObserver = io;
  }

  /* --------------------------------------------------------------------------
     Fluent hover reveal
     Moves radial highlight position based on cursor.
     -------------------------------------------------------------------------- */
  function initHoverReveal(scope) {
    var cards = Array.prototype.slice.call(scope.querySelectorAll('.ha-reveal'));

    if (!cards.length) return;

    cards.forEach(function (el) {
      if (el.__haHoverReady) return;

      el.__haHoverReady = true;

      el.addEventListener('pointermove', function (event) {
        var rect = el.getBoundingClientRect();

        el.style.setProperty('--ha-mx', event.clientX - rect.left + 'px');
        el.style.setProperty('--ha-my', event.clientY - rect.top + 'px');
      });

      el.addEventListener('pointerleave', function () {
        el.style.removeProperty('--ha-mx');
        el.style.removeProperty('--ha-my');
      });
    });
  }

  /* --------------------------------------------------------------------------
     Smooth anchors
     Makes internal buttons like #apply and #perks scroll cleanly.
     -------------------------------------------------------------------------- */
  function initSmoothAnchors(scope) {
    var links = Array.prototype.slice.call(scope.querySelectorAll('a[href^="#"]'));

    links.forEach(function (link) {
      if (link.__haSmoothReady) return;

      link.__haSmoothReady = true;

      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href');

        if (!href || href === '#') return;

        var target = null;

        try {
          target = document.querySelector(href);
        } catch (err) {
          target = null;
        }

        if (!target) return;

        event.preventDefault();

        target.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });

        try {
          window.history.pushState(null, '', href);
        } catch (err) {
          window.location.hash = href;
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Query-string auto scroll
     /pages/ambassador?apply=1 scrolls directly to form.
     -------------------------------------------------------------------------- */
  function initApplyScroll(scope) {
    if (!/[?&]apply=1/.test(window.location.search)) return;

    var form = scope.querySelector('#apply');

    if (!form || form.__haApplyScrolled) return;

    form.__haApplyScrolled = true;

    setTimeout(function () {
      form.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
    }, 250);
  }

  /* --------------------------------------------------------------------------
     Success notice scroll
     After Shopify contact form success, scrolls to confirmation message.
     -------------------------------------------------------------------------- */
  function initSuccessScroll(scope) {
    var notice = scope.querySelector('.ha-form-notice--ok');

    if (!notice || notice.__haSuccessScrolled) return;

    notice.__haSuccessScrolled = true;

    setTimeout(function () {
      notice.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center'
      });
    }, 250);
  }

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initAmbassadorPage(document);
  });

  window.addEventListener('load', function () {
    initAmbassadorPage(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAmbassadorPage(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    initAmbassadorPage(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    initAmbassadorPage(event.target);
  });
})();